import React, { useState, useEffect, useRef } from 'react';
import { FileText, Upload, Check, AlertCircle, Loader2, Mail, ExternalLink } from 'lucide-react';
import { trackEvent } from '../../services/telemetry';

const SUPPORTED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];

function isSupportedFile(name) {
  if (!name) return false;
  const lower = name.toLowerCase();
  return SUPPORTED_EXTENSIONS.some(ext => lower.endsWith(ext));
}

export default function TaskPane() {
  const [attachments, setAttachments] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, uploading, done, error
  const [errorMessage, setErrorMessage] = useState(null);
  const [fallbackUrl, setFallbackUrl] = useState(null);
  const dialogRef = useRef(null);
  const pendingFileRef = useRef(null);
  const fileSentRef = useRef(false);
  const handledRef = useRef(false);

  // Read attachments from the current email
  useEffect(() => {
    if (typeof Office === 'undefined' || !Office.context?.mailbox?.item) {
      return;
    }

    const item = Office.context.mailbox.item;
    const mailAttachments = item.attachments || [];

    // Filter to supported file types
    const supported = mailAttachments.filter(att =>
      att.attachmentType === Office.MailboxEnums?.AttachmentType?.File &&
      isSupportedFile(att.name)
    );

    setAttachments(supported);

    // Auto-select if exactly one supported attachment
    if (supported.length === 1) {
      setSelectedId(supported[0].id);
    }
  }, []);

  function handleProcess() {
    if (!selectedId || status === 'uploading') return;

    const attachment = attachments.find(a => a.id === selectedId);
    if (!attachment) return;

    // Reset once-only guards for this attempt so "Process Another" works.
    fileSentRef.current = false;
    handledRef.current = false;
    setStatus('uploading');
    setErrorMessage(null);
    setFallbackUrl(null);

    // Get attachment content as base64
    Office.context.mailbox.item.getAttachmentContentAsync(
      selectedId,
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          setStatus('error');
          setErrorMessage(result.error?.message || 'Failed to read attachment');
          return;
        }

        const content = result.value.content; // base64 string
        const contentType = attachment.contentType || 'application/octet-stream';

        // Store file data for the dialog to pick up
        pendingFileRef.current = {
          fileName: attachment.name,
          mimeType: contentType,
          content: content,
        };

        // Open the bridge dialog on the SWA domain
        const dialogUrl = `${window.location.origin}/outlook/bridge`;
        Office.context.ui.displayDialogAsync(
          dialogUrl,
          { width: 25, height: 25, displayInIframe: false },
          (asyncResult) => {
            if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
              setStatus('error');
              setErrorMessage(asyncResult.error?.message || 'Failed to open upload dialog');
              return;
            }

            const dialog = asyncResult.value;
            dialogRef.current = dialog;

            // Listen for messages from the dialog
            dialog.addEventHandler(
              Office.EventType.DialogMessageReceived,
              (arg) => {
                const message = arg.message;

                if (message === 'ready') {
                  // Send the file data once — a duplicate 'ready' would otherwise
                  // trigger a second upload in the dialog.
                  if (fileSentRef.current) return;
                  fileSentRef.current = true;
                  dialog.messageChild(JSON.stringify(pendingFileRef.current));
                } else if (message.startsWith('error:')) {
                  setStatus('error');
                  setErrorMessage(message.substring(6));
                  dialog.close();
                  dialogRef.current = null;
                } else {
                  // Message is upload data JSON — upload succeeded.
                  // Always close the dialog first: returning early on a duplicate delivery used to
                  // leave it stranded on "Upload complete / Closing...", which reads as a failure
                  // and prompts the CSR to re-send the mail (burning a second AI order number).
                  // Closing an already-closed dialog is a no-op.
                  dialog.close();
                  dialogRef.current = null;

                  // Handle once so a duplicate delivery can't open multiple tabs.
                  if (handledRef.current) return;
                  handledRef.current = true;

                  let uploadData;
                  try {
                    uploadData = JSON.parse(message);
                  } catch {
                    // Fallback for plain correlationId string (shouldn't happen)
                    uploadData = { correlationId: message };
                  }

                  // Reaching here means Outlook delivered the DialogMessageReceived handoff
                  // normally — the counterpart to Bridge.jsx's 'outlook_bridge_stalled'/
                  // 'outlook_bridge_upload_complete' events, so a drop rate can be computed
                  // (uploads that never show up here got dropped by Outlook).
                  trackEvent('outlook_handoff_received', {
                    correlationId: uploadData.correlationId,
                  });

                  // Build URL with upload data as params (system browser can't share localStorage with Outlook WebView)
                  const params = new URLSearchParams({ from: 'outlook', cid: uploadData.correlationId });
                  if (uploadData.aiOrderNumber) params.set('aiOrderNumber', uploadData.aiOrderNumber);
                  if (uploadData.sasUrl) params.set('sasUrl', uploadData.sasUrl);
                  if (uploadData.fileName) params.set('fileName', uploadData.fileName);
                  if (uploadData.mimeType) params.set('mimeType', uploadData.mimeType);
                  if (uploadData.officeViewerUrl) params.set('officeViewerUrl', uploadData.officeViewerUrl);
                  if (uploadData.canUseOfficeViewer) params.set('canUseOfficeViewer', '1');

                  const url = `${window.location.origin}/?${params.toString()}`;

                  // Open in system browser (not Outlook's embedded WebView)
                  let opened = false;
                  if (Office?.context?.ui?.openBrowserWindow) {
                    try {
                      Office.context.ui.openBrowserWindow(url);
                      opened = true;
                    } catch { /* fall through to window.open */ }
                  }
                  if (!opened) {
                    const newTab = window.open(url, '_blank');
                    if (!newTab) setFallbackUrl(url);
                  }

                  setStatus('done');
                }
              }
            );

            // Handle dialog closed by user
            dialog.addEventHandler(
              Office.EventType.DialogEventReceived,
              (arg) => {
                if (arg.error === 12006) {
                  // Dialog was closed by user — use functional update to avoid stale closure
                  setStatus(prev => prev === 'uploading' ? 'idle' : prev);
                  dialogRef.current = null;
                }
              }
            );
          }
        );
      }
    );
  }

  // No Office context — running in browser for dev/testing
  const isOutlookContext = typeof Office !== 'undefined' && Office.context?.mailbox?.item;

  if (!isOutlookContext) {
    return (
      <div className="min-h-screen bg-slate-900 p-4 flex flex-col items-center justify-center text-center">
        <Mail className="w-10 h-10 text-slate-600 mb-3" />
        <h2 className="text-white font-semibold mb-1">Order Processor — Outlook Add-in</h2>
        <p className="text-slate-400 text-xs">
          This page is the Outlook add-in task pane. Open it from within Outlook to process PO attachments.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-4 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-amber-400/10 rounded-lg flex items-center justify-center">
          <Upload className="w-4 h-4 text-amber-400" />
        </div>
        <div>
          <h1 className="text-white font-semibold text-sm">Order Processor</h1>
          <p className="text-slate-500 text-xs">Send attachment to process</p>
        </div>
      </div>

      {/* Content */}
      {status === 'done' ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mb-3">
            <Check className="w-6 h-6 text-emerald-400" />
          </div>
          <p className="text-white font-medium mb-1">Sent to Order Processor</p>
          {fallbackUrl ? (
            <>
              <p className="text-slate-400 text-xs mb-3">The browser tab couldn't open automatically. Click below to continue:</p>
              <a
                href={fallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-lg font-medium transition-colors"
              >
                Open Order Processor
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </>
          ) : (
            <p className="text-slate-400 text-xs">Check your browser tab to review the order.</p>
          )}
          <button
            onClick={() => { setStatus('idle'); setFallbackUrl(null); setSelectedId(attachments.length === 1 ? attachments[0].id : null); }}
            className="mt-4 w-full py-2.5 rounded-lg font-medium text-sm bg-amber-400 hover:bg-amber-500 text-slate-900 transition-all"
          >
            Process Another
          </button>
        </div>
      ) : status === 'error' ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mb-3">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <p className="text-red-400 font-medium text-sm mb-1">Upload Failed</p>
          <p className="text-slate-400 text-xs mb-4">{errorMessage}</p>
          <button
            onClick={() => setStatus('idle')}
            className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : attachments.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <Mail className="w-10 h-10 text-slate-600 mb-3" />
          <p className="text-slate-400 text-sm">No supported attachments found</p>
          <p className="text-slate-600 text-xs mt-1">Supported: PDF, Word, Excel, Text</p>
        </div>
      ) : (
        <>
          {/* Attachment list */}
          <div className="space-y-1.5 mb-4">
            {attachments.map((att) => {
              const isSelected = att.id === selectedId;
              return (
                <button
                  key={att.id}
                  onClick={() => setSelectedId(att.id)}
                  disabled={status === 'uploading'}
                  className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-colors ${
                    isSelected
                      ? 'bg-amber-400/10 border border-amber-400/30'
                      : 'bg-slate-800 border border-slate-700 hover:border-slate-600'
                  } ${status === 'uploading' ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FileText className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-amber-400' : 'text-slate-500'}`} />
                  <span className={`text-sm truncate ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                    {att.name}
                  </span>
                  {isSelected && (
                    <Check className="w-3.5 h-3.5 text-amber-400 ml-auto flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Process button */}
          <button
            onClick={handleProcess}
            disabled={!selectedId || status === 'uploading'}
            className={`w-full py-2.5 rounded-lg font-medium text-sm transition-all ${
              !selectedId || status === 'uploading'
                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                : 'bg-amber-400 hover:bg-amber-500 text-slate-900'
            }`}
          >
            {status === 'uploading' ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </span>
            ) : (
              'Process PO'
            )}
          </button>
        </>
      )}
    </div>
  );
}
